import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type Project } from '@/api/client';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const SEARCH_DEBOUNCE_MS = 300;
const RESULTS_LIMIT = 20;

export type FonotekaProjectDialogProps = {
  open: boolean;
  /** Проекты трекера, у которых место в Фонотеке уже есть: в списке серые и не выбираются */
  takenProjectIds: ReadonlySet<string>;
  saving: boolean;
  error?: string | null;
  onClose: () => void;
  onPick: (project: Pick<Project, 'id' | 'name'>) => void;
};

function projectsOf(page: Project[] | { items: Project[] }): Project[] {
  return Array.isArray(page) ? page : page.items;
}

/** «Новый проект» в Фонотеке: проект трекера ищется по названию; у выбранного появляется место в Фонотеке с его именем */
export function FonotekaProjectDialog({ open, takenProjectIds, saving, error = null, onClose, onPick }: FonotekaProjectDialogProps) {
  const [value, setValue] = useState('');
  const q = useDebouncedValue(value.trim(), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (open) setValue('');
  }, [open]);

  const search = useQuery({
    queryKey: ['fonoteka', 'project-search', q],
    queryFn: () => api.projects.list({ q, limit: RESULTS_LIMIT }),
    enabled: open && q.length > 0,
    placeholderData: keepPreviousData,
  });
  const projects = useMemo(() => (q && search.data ? projectsOf(search.data) : []), [q, search.data]);

  return (
    <Modal open={open} onClose={() => !saving && onClose()} title="Новый проект" headerVariant="island" size="sm">
      <div className="flex flex-col gap-3 p-1">
        <Input
          label="Проект"
          placeholder="Название проекта"
          value={value}
          error={error ?? undefined}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          autoFocus
        />
        <div className="flex max-h-72 flex-col overflow-y-auto" role="listbox" aria-label="Найденные проекты">
          {!q ? null : search.isError ? (
            <p className="px-2 py-3 text-sm text-destructive">Не удалось найти проекты</p>
          ) : search.isPending ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">Ищем…</p>
          ) : projects.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">Ничего не найдено</p>
          ) : (
            projects.map((project) => {
              const taken = takenProjectIds.has(project.id);
              return (
                <button
                  key={project.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  disabled={saving || taken}
                  onClick={() => onPick(project)}
                  className="flex h-9 shrink-0 items-center justify-between gap-3 rounded-md px-2 text-left text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                >
                  <span className="min-w-0 truncate">{project.name}</span>
                  {taken ? <span className="shrink-0 text-xs text-muted-foreground">уже в Фонотеке</span> : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}
